import { apiClient } from '../apiClient';

import type { AssessmentRubric } from './rubric';

// --- Trainer batch list ---

export interface TrainerBatch {
  id: string;
  name: string;
  org_name: string;
  student_count: number;
  start_date: string;
  end_date: string;
  progress_pct: number;
}

// --- Batch overview ---

export interface BatchSummary {
  id: string;
  name: string;
  student_count: number;
}

export interface BatchStats {
  avg_progress: number;
  total_sessions: number;
  upcoming_sessions: number;
  pending_grading: number;
  attendance_avg: number;
  pending_trainer_reply: boolean;
}

export interface StudentSummary {
  id: string;
  username: string;
  avatar_url: string;
  progress_pct: number;
  attendance_pct: number;
  last_active: string | null;
  completed_topics: number;
  total_topics: number;
  is_flagged: boolean;
  flagged_at: string | null;
}

export interface TopicProgress {
  topic_id: string;
  topic_title: string;
  module_title: string;
  completed: boolean;
}

export interface SessionAttendance {
  session_id: string;
  session_title: string;
  scheduled_at: string;
  status: 'present' | 'absent' | 'late' | null;
}

export interface AssignmentStatus {
  assessment_id: string;
  title: string;
  max_score: number;
  submission_url: string | null;
  score: number | null;
  submitted_at: string | null;
  graded_at: string | null;
}

export interface StudentDetail {
  topics: TopicProgress[];
  attendance: SessionAttendance[];
  assignments: AssignmentStatus[];
}

export interface UpcomingSession {
  id: string;
  title: string;
  scheduled_at: string;
  topic_title: string;
  topic_id: string;
  attendance_taken: boolean;
  duration_mins: number | null;
  meeting_url: string;
}

export interface BatchOverview {
  batch: BatchSummary;
  stats: BatchStats;
  students: StudentSummary[];
  upcoming_sessions: UpcomingSession[];
}

// --- Trainer Dashboard ---

export interface TrainerInfo {
  name: string;
  avatar_url: string;
}

export interface DashboardStats {
  total_students: number;
  total_batches: number;
  attendance_avg: number;
  pending_grading: number;
}

export interface DashboardBatch {
  id: string;
  name: string;
  student_count: number;
  progress_pct: number;
  attendance_avg: number;
  pending_grading: number;
  session_count: number;
  at_risk_count: number;
}

export interface DashboardOrg {
  org_id: string;
  org_name: string;
  batches: DashboardBatch[];
}

export interface DashboardSession {
  id: string;
  title: string;
  scheduled_at: string;
  batch_id: string;
  batch_name: string;
  org_name: string;
}

export interface TrainerDashboard {
  trainer: TrainerInfo;
  stats: DashboardStats;
  orgs: DashboardOrg[];
  today_sessions: DashboardSession[];
  at_risk_count: number;
  unanswered_questions: number;
}

// --- Session ---

export interface SessionRow {
  id: string;
  batch_topic_id: string;
  title: string;
  scheduled_at?: string;
  meeting_url?: string;
  recording_url?: string;
  duration_mins?: number;
  trainer_id: string;
}

export interface CreateSessionRequest {
  batch_topic_id: string;
  title: string;
  scheduled_at: string;
  meeting_url: string;
  duration_mins?: number;
}

export interface UpdateSessionRequest {
  title: string;
  scheduled_at: string;
  meeting_url: string;
  recording_url: string;
  duration_mins?: number;
}

// --- Attendance ---

export interface AttendanceEntry {
  user_id: string;
  status: 'present' | 'absent' | 'late';
}

export interface MarkAttendanceRequest {
  attendance: AttendanceEntry[];
}

export interface AttendanceEntryRow {
  user_id: string;
  username: string;
  status: 'present' | 'absent' | 'late';
}

export interface PendingSubmission {
  submission_id: string;
  assessment_id: string;
  assessment_title: string;
  max_score: number;
  student_id: string;
  student_name: string;
  /**
   * `null` when the grade has no artifact behind it — a viva, a whiteboard
   * round. Not `''`: the server stopped COALESCEing the two together so the
   * grading inbox can tell "nothing was handed in" from "an empty link".
   */
  submission_url: string | null;
  submitted_at: string;
  /** Present when the assessment was authored with a marking scheme. */
  rubric?: AssessmentRubric;
}

export interface GradeSubmissionRequest {
  score: number;
  feedback: string;
  /**
   * Per-criterion marks, keyed by the criterion ids in the assessment's
   * rubric. When present the server derives `score` as their sum and ignores
   * the `score` field above, so the two can never drift apart.
   */
  rubric_scores?: Record<string, number>;
}

// --- Resources ---

export interface ResourceRow {
  id: string;
  batch_topic_id: string;
  title: string;
  type: string;
  url: string;
  uploaded_by: string;
}

export interface CreateResourceRequest {
  title: string;
  type: string;
  url: string;
}

// --- Response types ---

export interface StatusResponse {
  status: string;
}

export interface Announcement {
  id: string;
  content: string;
  created_by: string;
  creator_name: string;
  created_at: string;
}

// --- API methods ---

/** What sort of work a row is. */
/**
 * Exhaustive: KIND_ICON and KIND_LABEL are Records over it, so a new kind
 * cannot ship without the two screens that render one deciding what it looks
 * like. 'viva' is work with nothing handed in — see migration 202.
 */
export type WorkKind = 'practice' | 'assignment' | 'quiz' | 'project' | 'viva';

/** Coarse on purpose — a trainer scanning thirty rows sorts them at a glance. */
export type WorkStatus = 'not_started' | 'in_progress' | 'submitted' | 'done';

/**
 * How the work came to exist. Absent when nothing was recorded, which is NOT
 * the same as "clean" — the telemetry only covers work done since it shipped.
 */
export interface WorkIntegrity {
  signal: // Practice, from the editor.
    | 'self_solved'
    | 'assisted'
    | 'pasted'
    // Assignments and projects, from the repository's commit history.
    | 'built_over_time'
    | 'one_sitting'
    | 'one_commit'
    | 'insufficient';
  /** Practice only. */
  largest_paste_ratio: number;
  /** Practice only. */
  active_mins: number;
  /** Set instead of the two fields above for repository-backed work. */
  repo?: WorkRepoProvenance;
}

/**
 * The evidence behind a repository-shaped signal.
 *
 * Flags travel separately from the signal: a fork with two hundred honest
 * commits on top is a different conversation from a fork with none, and one
 * label cannot hold both.
 */
export interface WorkRepoProvenance {
  commits: number;
  span_days: number;
  repo_url?: string;
  is_fork: boolean;
  fork_parent?: string;
  shared_authorship: boolean;
  predates_assignment: boolean;
  authors?: string[];
  /** Why we could not look, in words a trainer can act on. */
  fetch_error?: string;
  checked_at?: string;
}

/** What one provenance run did. */
export interface ProvenanceRunResult {
  checked: number;
  read: number;
  unreadable: number;
  duration_ms: number;
  rate_limited: boolean;
  skipped: number;
  note?: string;
}

/** Whether the course set this work, or the student went and found it. */
export type WorkSource = 'course' | 'self';

export interface WorkItem {
  id: string;
  kind: WorkKind;
  title: string;
  source: WorkSource;
  module_title?: string;
  topic_title?: string;
  status: WorkStatus;
  score?: number;
  max_score?: number;
  last_activity_at?: string;
  verdict?: string;
  attempts?: number;
  submission_url?: string;
  integrity?: WorkIntegrity;
}

export interface WorkCounts {
  total: number;
  done: number;
  submitted: number;
  not_started: number;
  needs_asking: number;
}

/** Everything one student has been set and done on a course, in one list. */
export interface StudentWork {
  items: WorkItem[];
  /** Course work only, so "6 of 9 done" stays a statement about the syllabus. */
  counts: WorkCounts;
  /** Practice found outside the course. All zeroes for most students. */
  self_counts: WorkCounts;
}

/** The work itself, independent of who did it. */
export interface WorkItemMeta {
  id: string;
  kind: WorkKind;
  title: string;
  module_title?: string;
  topic_title?: string;
  max_score?: number;
  problem_slug?: string;
  /**
   * The marking scheme, when the assessment was authored with one. Present on
   * this payload because this screen is where a viva is marked: the roster and
   * the criteria have to arrive together or the grader is entering marks
   * against a scheme they cannot see.
   */
  rubric?: AssessmentRubric;
}

/**
 * One student's standing on one piece of work. Every enrolled student appears,
 * including those who never opened it — that absence is the point of the
 * screen, not an edge case.
 */
export interface WorkItemStudent {
  user_id: string;
  name: string;
  status: WorkStatus;
  score?: number;
  last_activity_at?: string;
  attempts?: number;
  submission_url?: string;
  integrity?: WorkIntegrity;
  /** Index into WorkItemDetail.clusters, when this student is in one. */
  cluster_id?: number;
}

/**
 * Students whose submissions share distinctive code, connected transitively.
 * Grouped rather than listed pair-by-pair: three students with one solution is
 * one conversation, not three.
 */
/**
 * Which channel found a group, and so which sentence it deserves.
 *
 * `shared_code` — these submissions share code distinctive within this cohort.
 * `same_file`   — these submissions are the same file, down to the variable
 *                 names and the spacing, which independent authors do not do.
 *
 * The distinction carries the weight on short problems, where every correct
 * answer has the same shape and "shares distinctive code" would accuse a class
 * of having learned the pattern.
 */
export type ClusterKind = 'shared_code' | 'same_file';

export interface SimilarityCluster {
  id: number;
  members: string[];
  names: string[];
  /** The strongest pair inside the group, 0–1. For same_file, the weakest. */
  top_score: number;
  kind: ClusterKind;
}

export interface WorkItemCounts {
  total: number;
  done: number;
  in_progress: number;
  submitted: number;
  not_started: number;
  needs_asking: number;
  in_clusters: number;
  /** Started, but with no recorded process. Published rather than hidden. */
  not_recorded: number;
}

/** One piece of work, every student against it. */
export interface WorkItemDetail {
  item: WorkItemMeta;
  students: WorkItemStudent[];
  clusters: SimilarityCluster[];
  counts: WorkItemCounts;
}

/** One practice submission, exactly as the student sent it. */
export interface ArtifactCode {
  submission_id: string;
  language: string;
  code: string;
  verdict: string;
  runtime_ms?: number;
  memory_kb?: number;
  passed_cases?: number;
  total_cases?: number;
  error_msg?: string;
  submitted_at: string;
}

/** One line of the history behind the final answer. Carries no code. */
export interface ArtifactAttempt {
  submission_id: string;
  verdict: string;
  language: string;
  passed_cases?: number;
  total_cases?: number;
  created_at: string;
}

/** One quiz question, with what they picked beside what was right. */
export interface ArtifactQuizAnswer {
  question_id: string;
  question_text: string;
  options: string[];
  correct_option: number;
  /** Absent when they never answered it — not the same as answering wrongly. */
  chosen_option?: number;
  points: number;
}

/** An assignment or project: a link, plus whatever the grader said. */
export interface ArtifactHandin {
  submission_url: string;
  score?: number;
  max_score?: number;
  feedback?: string;
  submitted_at?: string;
  graded_at?: string;
}

/** One student's actual work. Exactly one payload is set, chosen by kind. */
export interface WorkArtifact {
  kind: WorkKind;
  student_id: string;
  student_name: string;
  item_title: string;
  code?: ArtifactCode;
  attempts?: ArtifactAttempt[];
  quiz?: ArtifactQuizAnswer[];
  handin?: ArtifactHandin;
  empty: boolean;
}

export interface ArtifactSide {
  student_id: string;
  student_name: string;
  code: ArtifactCode;
}

/** The two submissions behind one similarity pair — the ones actually compared. */
export interface ArtifactComparison {
  item_title: string;
  score: number;
  shared_count: number;
  a: ArtifactSide;
  b: ArtifactSide;
}

export const trainerApi = {
  getDashboard: () => apiClient.get<TrainerDashboard>('/api/v1/trainer/dashboard'),

  listBatches: () => apiClient.get<TrainerBatch[]>('/api/v1/trainer/batches'),

  getBatchOverview: (batchId: string) =>
    apiClient.get<BatchOverview>(`/api/v1/trainer/batches/${batchId}/overview`),

  createSession: (req: CreateSessionRequest) =>
    apiClient.post<SessionRow>('/api/v1/trainer/sessions', req),

  updateSession: (id: string, req: UpdateSessionRequest) =>
    apiClient.put<SessionRow>(`/api/v1/trainer/sessions/${id}`, req),

  markAttendance: (sessionId: string, req: MarkAttendanceRequest) =>
    apiClient.post<StatusResponse>(`/api/v1/trainer/sessions/${sessionId}/attendance`, req),

  getAttendance: (sessionId: string) =>
    apiClient.get<AttendanceEntryRow[]>(`/api/v1/trainer/sessions/${sessionId}/attendance`),

  listPendingSubmissions: (batchId: string) =>
    apiClient.get<PendingSubmission[]>(`/api/v1/trainer/batches/${batchId}/submissions/pending`),

  /**
   * Under /trainer/grading rather than /trainer/assessments/... — the latter
   * prefix is mounted by the assessments module, and this endpoint used to
   * 404 there. See the routing comment in services/api/internal/trainer/handler.go.
   *
   * assessmentId is no longer part of the path (the submission id identifies
   * the paper), but stays in the signature so callers keep passing what they
   * already have and nothing downstream has to change shape.
   */
  gradeSubmission: (_assessmentId: string, submissionId: string, req: GradeSubmissionRequest) =>
    apiClient.post<StatusResponse>(`/api/v1/trainer/grading/submissions/${submissionId}`, req),

  /**
   * Grades a *student* rather than a submission, creating the submission row
   * if there isn't one. This is how work with no artifact — a viva, an
   * in-person round — gets a mark at all.
   */
  gradeStudentDirect: (assessmentId: string, studentId: string, req: GradeSubmissionRequest) =>
    apiClient.post<StatusResponse>(
      `/api/v1/trainer/grading/assessments/${assessmentId}/students/${studentId}`,
      req,
    ),

  getStudentDetail: (batchId: string, studentId: string) =>
    apiClient.get<StudentDetail>(`/api/v1/trainer/batches/${batchId}/students/${studentId}/detail`),

  getStudentWork: (batchId: string, studentId: string) =>
    apiClient.get<StudentWork>(`/api/v1/trainer/batches/${batchId}/students/${studentId}/work`),

  getWorkItem: (batchId: string, assessmentId: string) =>
    apiClient.get<WorkItemDetail>(`/api/v1/trainer/batches/${batchId}/work-items/${assessmentId}`),

  getWorkArtifact: (batchId: string, assessmentId: string, studentId: string) =>
    apiClient.get<WorkArtifact>(
      `/api/v1/trainer/batches/${batchId}/work-items/${assessmentId}/students/${studentId}`,
    ),

  compareSubmissions: (batchId: string, assessmentId: string, a: string, b: string) =>
    apiClient.get<ArtifactComparison>(
      `/api/v1/trainer/batches/${batchId}/work-items/${assessmentId}/compare`,
      { params: { a, b } },
    ),

  refreshProvenance: (batchId: string) =>
    apiClient.post<ProvenanceRunResult>(`/api/v1/trainer/batches/${batchId}/provenance/refresh`),

  getSubmissionCode: (batchId: string, submissionId: string) =>
    apiClient.get<ArtifactCode>(
      `/api/v1/trainer/batches/${batchId}/submissions/${submissionId}/code`,
    ),

  toggleFlag: (batchId: string, studentId: string, flagged: boolean) =>
    apiClient.put<{ flagged: boolean }>(
      `/api/v1/trainer/batches/${batchId}/students/${studentId}/flag`,
      { flagged },
    ),

  notifyStudent: (batchId: string, studentId: string, message: string) =>
    apiClient.post<{ status: string }>(
      `/api/v1/trainer/batches/${batchId}/students/${studentId}/notify`,
      { message },
    ),

  listAnnouncements: (batchId: string) =>
    apiClient.get<Announcement[]>(`/api/v1/trainer/batches/${batchId}/announcements`),

  addResource: (topicId: string, req: CreateResourceRequest) =>
    apiClient.post<ResourceRow>(`/api/v1/trainer/topics/${topicId}/resources`, req),

  deleteResource: (id: string) => apiClient.delete<void>(`/api/v1/trainer/resources/${id}`),
};

// ─── Trainer content authoring ───────────────────────────────────────────────

export interface CurriculumTopic {
  id: string;
  title: string;
  description?: string;
  topic_type: 'lecture' | 'lab' | 'project';
  sort_order: number;
  estimated_hours?: number;
  session_count: number;
  resource_count: number;
  assessment_count: number;
  has_source_topic: boolean;
  /**
   * When this batch's trainer released the editorials for this topic's
   * problems to the whole batch. Null means not released — and a timestamp
   * rather than a flag so the screen can say when it happened.
   *
   * Always present (the API does not omit it when null), so an object spread
   * of an updated topic over a stored one actually clears it on un-release.
   */
  editorial_released_at?: string | null;
}

export interface CurriculumModule {
  id: string;
  title: string;
  skill_label?: string;
  phase: string;
  sort_order: number;
  estimated_days?: number;
  topics: CurriculumTopic[];
}

export type SessionType = 'live_class' | 'recorded' | 'office_hours' | 'lab';
export type SessionStatus = 'scheduled' | 'live' | 'completed' | 'cancelled';

export interface TopicSession {
  id: string;
  batch_topic_id: string;
  title: string;
  session_type?: SessionType;
  status?: SessionStatus;
  scheduled_at?: string;
  meeting_url?: string;
  recording_url?: string;
  duration_mins?: number;
  attendance_required?: boolean;
  notes?: string;
  trainer_id?: string;
}

export interface UpsertSessionRequest {
  title: string;
  session_type?: SessionType;
  status?: SessionStatus;
  scheduled_at?: string;
  meeting_url?: string;
  recording_url?: string;
  duration_mins?: number;
  attendance_required?: boolean;
  notes?: string;
}

export interface TopicResource {
  id: string;
  batch_topic_id: string;
  title: string;
  type: string;
  url: string;
  description?: string;
  is_required?: boolean;
  uploaded_by?: string;
}

export interface UpsertResourceRequest {
  title: string;
  type: string;
  url: string;
  description?: string;
  is_required?: boolean;
}

export interface TopicAssessment {
  id: string;
  batch_topic_id: string;
  title: string;
  description?: string;
  type: 'mcq_quiz' | 'coding_challenge' | 'project_assignment' | 'viva';
  max_score: number;
  due_date?: string;
  difficulty?: string;
  asked_in?: string[];
  problem_slug?: string;
  assignment_kind?: 'assignment' | 'project';
  duration_mins?: number;
  max_attempts?: number;
  opens_at?: string;
  closes_at?: string;
  shuffle_questions?: boolean;
  shuffle_options?: boolean;
  questions_per_attempt?: number;
  group_key?: string;
  group_label?: string;
  rubric?: AssessmentRubric;
  due_relative_days?: number;
  created_by?: string;
  created_at: string;
}

// Canonical definition lives in ./rubric so the student LMS API can use the
// same shape without importing the trainer API. Re-exported here because
// every existing consumer imports it from this module.
export type { RubricCriterion, AssessmentRubric } from './rubric';

export interface UpsertAssessmentRequest {
  title: string;
  description?: string;
  type: 'mcq_quiz' | 'coding_challenge' | 'project_assignment' | 'viva';
  max_score: number;
  due_date?: string;
  difficulty?: string;
  asked_in?: string[];
  problem_slug?: string;
  assignment_kind?: 'assignment' | 'project';
  duration_mins?: number;
  max_attempts?: number;
  opens_at?: string;
  closes_at?: string;
  shuffle_questions?: boolean;
  shuffle_options?: boolean;
  questions_per_attempt?: number;
  group_key?: string;
  group_label?: string;
  rubric?: AssessmentRubric;
  due_relative_days?: number;
}

export interface CreateModuleRequest {
  title: string;
  skill_label?: string;
  phase?: string;
  sort_order: number;
  estimated_days?: number;
}

export interface UpdateModuleRequest {
  title?: string;
  skill_label?: string;
  phase?: string;
  sort_order?: number;
  estimated_days?: number;
}

export interface CreateTopicRequest {
  title: string;
  description?: string;
  topic_type: 'lecture' | 'lab' | 'project';
  sort_order: number;
  estimated_hours?: number;
}

export interface UpdateTopicRequest {
  title?: string;
  description?: string;
  topic_type?: 'lecture' | 'lab' | 'project';
  sort_order?: number;
  estimated_hours?: number;
}

export interface TopicVisualization {
  id: string;
  curriculum_topic_id: string;
  kind: 'concept' | 'problem';
  leetcode_id?: number;
  difficulty?: 'easy' | 'medium' | 'hard';
  problem_slug?: string;
  schema_version: number;
  script: unknown; // validated server-side against StepScript from Visualizer/types.ts
  /**
   * Storage is fork-on-write per batch (Task 11/12): `true` means this batch
   * has its own independently-edited copy; `false` means this response is
   * still the shared curriculum template's content — no batch-specific row
   * exists yet, and the first save here will fork one.
   */
  is_customized: boolean;
}

export interface UpsertVisualizationRequest {
  kind: 'concept' | 'problem';
  leetcode_id?: number;
  difficulty?: 'easy' | 'medium' | 'hard';
  problem_slug?: string;
  schema_version: number;
  script: unknown;
}

export const trainerContentApi = {
  getCurriculum: (batchId: string) =>
    apiClient.get<CurriculumModule[]>(`/api/v1/trainer/batches/${batchId}/curriculum`),

  createModule: (batchId: string, req: CreateModuleRequest) =>
    apiClient.post<CurriculumModule>(`/api/v1/trainer/batches/${batchId}/curriculum/modules`, req),
  updateModule: (batchId: string, moduleId: string, req: UpdateModuleRequest) =>
    apiClient.patch<CurriculumModule>(
      `/api/v1/trainer/batches/${batchId}/curriculum/modules/${moduleId}`,
      req,
    ),
  deleteModule: (batchId: string, moduleId: string) =>
    apiClient.delete(`/api/v1/trainer/batches/${batchId}/curriculum/modules/${moduleId}`),

  createTopic: (batchId: string, moduleId: string, req: CreateTopicRequest) =>
    apiClient.post<CurriculumTopic>(
      `/api/v1/trainer/batches/${batchId}/curriculum/modules/${moduleId}/topics`,
      req,
    ),
  updateTopic: (batchId: string, topicId: string, req: UpdateTopicRequest) =>
    apiClient.patch<CurriculumTopic>(
      `/api/v1/trainer/batches/${batchId}/curriculum/topics/${topicId}`,
      req,
    ),
  deleteTopic: (batchId: string, topicId: string) =>
    apiClient.delete(`/api/v1/trainer/batches/${batchId}/curriculum/topics/${topicId}`),

  /**
   * Opens (or withdraws) this topic's editorials for everyone enrolled in the
   * batch, whether or not they solved anything. Scoped to the batch: releasing
   * here changes nothing for another college running the same course.
   */
  setEditorialRelease: (batchId: string, topicId: string, released: boolean) =>
    apiClient.put<CurriculumTopic>(
      `/api/v1/trainer/batches/${batchId}/curriculum/topics/${topicId}/editorial-release`,
      { released },
    ),

  listSessions: (batchId: string, topicId: string) =>
    apiClient.get<TopicSession[]>(`/api/v1/trainer/batches/${batchId}/topics/${topicId}/sessions`),
  createSession: (batchId: string, topicId: string, req: UpsertSessionRequest) =>
    apiClient.post<TopicSession>(
      `/api/v1/trainer/batches/${batchId}/topics/${topicId}/sessions`,
      req,
    ),
  updateSession: (batchId: string, topicId: string, sessionId: string, req: UpsertSessionRequest) =>
    apiClient.patch<TopicSession>(
      `/api/v1/trainer/batches/${batchId}/topics/${topicId}/sessions/${sessionId}`,
      req,
    ),
  deleteSession: (batchId: string, topicId: string, sessionId: string) =>
    apiClient.delete(`/api/v1/trainer/batches/${batchId}/topics/${topicId}/sessions/${sessionId}`),

  listResources: (batchId: string, topicId: string) =>
    apiClient.get<TopicResource[]>(
      `/api/v1/trainer/batches/${batchId}/topics/${topicId}/resources`,
    ),
  createResource: (batchId: string, topicId: string, req: UpsertResourceRequest) =>
    apiClient.post<TopicResource>(
      `/api/v1/trainer/batches/${batchId}/topics/${topicId}/resources`,
      req,
    ),
  updateResource: (
    batchId: string,
    topicId: string,
    resourceId: string,
    req: UpsertResourceRequest,
  ) =>
    apiClient.patch<TopicResource>(
      `/api/v1/trainer/batches/${batchId}/topics/${topicId}/resources/${resourceId}`,
      req,
    ),
  deleteResource: (batchId: string, topicId: string, resourceId: string) =>
    apiClient.delete(
      `/api/v1/trainer/batches/${batchId}/topics/${topicId}/resources/${resourceId}`,
    ),

  listAssessments: (batchId: string, topicId: string) =>
    apiClient.get<TopicAssessment[]>(
      `/api/v1/trainer/batches/${batchId}/topics/${topicId}/assessments`,
    ),
  createAssessment: (batchId: string, topicId: string, req: UpsertAssessmentRequest) =>
    apiClient.post<TopicAssessment>(
      `/api/v1/trainer/batches/${batchId}/topics/${topicId}/assessments`,
      req,
    ),
  updateAssessment: (
    batchId: string,
    topicId: string,
    assessmentId: string,
    req: UpsertAssessmentRequest,
  ) =>
    apiClient.patch<TopicAssessment>(
      `/api/v1/trainer/batches/${batchId}/topics/${topicId}/assessments/${assessmentId}`,
      req,
    ),
  deleteAssessment: (batchId: string, topicId: string, assessmentId: string) =>
    apiClient.delete(
      `/api/v1/trainer/batches/${batchId}/topics/${topicId}/assessments/${assessmentId}`,
    ),

  getVisualization: (batchId: string, topicId: string) =>
    apiClient.get<TopicVisualization | null>(
      `/api/v1/trainer/batches/${batchId}/topics/${topicId}/visualization`,
    ),
  upsertVisualization: (batchId: string, topicId: string, req: UpsertVisualizationRequest) =>
    apiClient.put<TopicVisualization>(
      `/api/v1/trainer/batches/${batchId}/topics/${topicId}/visualization`,
      req,
    ),
  deleteVisualization: (batchId: string, topicId: string) =>
    apiClient.delete(`/api/v1/trainer/batches/${batchId}/topics/${topicId}/visualization`),
};
